/**
 * Mega Menu V2 — sub-link hover swaps product cards
 * Desktop: hover nav item opens panel; hover sub-link swaps right product cards
 * Mobile: tap accordion — text sub-links only, no images
 */

(function () {
  'use strict';

  const OPEN_DELAY  = 80;   // ms delay before opening on mouseenter
  const CLOSE_DELAY = 260;  // ms delay before closing after mouseleave

  // =========================================================================
  // Desktop MegaNav
  // =========================================================================
  class MegaNav {
    constructor(el) {
      this.nav       = el;
      this.backdrop  = document.querySelector('[data-mega-backdrop]');
      this.items     = Array.from(el.querySelectorAll('[data-mega-item]'));
      this.active    = null;
      this._openTimer  = null;
      this._closeTimer = null;

      this._bind();
    }

    _bind() {
      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger]');
        const panel   = item.querySelector('[data-mega-panel]');
        if (!trigger || !panel) return;

        // --- Nav item hover ---
        item.addEventListener('mouseenter', () => {
          clearTimeout(this._closeTimer);
          clearTimeout(this._openTimer);
          this._openTimer = setTimeout(() => this._open(item, trigger, panel), OPEN_DELAY);
        });

        item.addEventListener('mouseleave', () => {
          clearTimeout(this._openTimer);
          this._closeTimer = setTimeout(() => {
            if (this.active === item) this._close();
          }, CLOSE_DELAY);
        });

        // --- Sub-link hover swaps product panel ---
        const subLinks = panel.querySelectorAll('[data-sub-link]');
        const subPanels = panel.querySelectorAll('[data-sub-panel]');

        subLinks.forEach((subLink) => {
          subLink.addEventListener('mouseenter', () => {
            const id = subLink.getAttribute('data-sub-link');

            // Update active sub-link styling
            subLinks.forEach((sl) => sl.classList.remove('mega-panel__sub-link--active'));
            subLink.classList.add('mega-panel__sub-link--active');

            // Show matching product panel, hide others
            subPanels.forEach((sp) => {
              if (sp.getAttribute('data-sub-panel') === id) {
                sp.classList.add('mega-panel__products--visible');
                sp.setAttribute('aria-hidden', 'false');
              } else {
                sp.classList.remove('mega-panel__products--visible');
                sp.setAttribute('aria-hidden', 'true');
              }
            });
          });
        });

        // --- Keyboard ---
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.active === item ? this._close() : this._open(item, trigger, panel);
          }
          if (e.key === 'Escape') { this._close(); trigger.focus(); }
        });

        panel.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { this._close(); trigger.focus(); }
        });
      });

      // Backdrop click closes
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this._close());
      }

      // Outside click closes
      document.addEventListener('click', (e) => {
        if (!this.nav.contains(e.target)) this._close();
      });
    }

    _open(item, trigger, panel) {
      if (this.active && this.active !== item) this._close(true); // instant close previous

      this.active = item;
      item.classList.add('mega-nav__item--active');
      trigger.setAttribute('aria-expanded', 'true');

      panel.removeAttribute('hidden');
      // Force reflow for CSS transition
      panel.getBoundingClientRect();
      panel.classList.add('mega-panel--visible');

      if (this.backdrop) this.backdrop.classList.add('mega-nav-backdrop--visible');
    }

    _close(instant = false) {
      if (!this.active) return;
      const item    = this.active;
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel   = item.querySelector('[data-mega-panel]');

      this.active = null;
      item.classList.remove('mega-nav__item--active');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (this.backdrop) this.backdrop.classList.remove('mega-nav-backdrop--visible');

      if (!panel) return;
      panel.classList.remove('mega-panel--visible');

      if (instant) {
        panel.setAttribute('hidden', '');
      } else {
        const done = () => {
          panel.setAttribute('hidden', '');
          panel.removeEventListener('transitionend', done);
        };
        panel.addEventListener('transitionend', done);
      }
    }
  }

  // =========================================================================
  // Mobile accordion
  // =========================================================================
  class MegaMobileNav {
    constructor(el) {
      this.nav   = el;
      this.items = Array.from(el.querySelectorAll('[data-mobile-item]'));
      this._bind();
    }

    _bind() {
      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-mobile-trigger]');
        const panel   = item.querySelector('[data-mobile-panel]');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('mega-mobile-nav__item--open');

          // Close all
          this.items.forEach((i) => {
            i.classList.remove('mega-mobile-nav__item--open');
            const t = i.querySelector('[data-mobile-trigger]');
            const p = i.querySelector('[data-mobile-panel]');
            if (t) t.setAttribute('aria-expanded', 'false');
            if (p) p.setAttribute('hidden', '');
          });

          // Toggle clicked
          if (!isOpen) {
            item.classList.add('mega-mobile-nav__item--open');
            trigger.setAttribute('aria-expanded', 'true');
            panel.removeAttribute('hidden');
          }
        });
      });
    }
  }

  // =========================================================================
  // Boot
  // =========================================================================
  function init() {
    const desktopNav = document.querySelector('[data-mega-nav]');
    if (desktopNav) new MegaNav(desktopNav);

    const mobileNav = document.querySelector('[data-mega-mobile-nav]');
    if (mobileNav) new MegaMobileNav(mobileNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor changes
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

})();